export interface LatLngPoint {
  lat: number;
  lng: number;
}

function toNum(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : null;
}

/** Read lat/lng from Firestore shapes: {lat,lng}, {latitude,longitude}, GeoPoint, nested coordinates */
export function extractLatLng(source: unknown): LatLngPoint | null {
  if (!source || typeof source !== 'object') return null;
  const o = source as Record<string, unknown>;

  const lat =
    toNum(o.lat) ??
    toNum(o.latitude) ??
    toNum((o.coordinates as Record<string, unknown> | undefined)?.lat) ??
    toNum((o.coordinates as Record<string, unknown> | undefined)?.latitude);

  const lng =
    toNum(o.lng) ??
    toNum(o.longitude) ??
    toNum((o.coordinates as Record<string, unknown> | undefined)?.lng) ??
    toNum((o.coordinates as Record<string, unknown> | undefined)?.longitude);

  if (lat == null || lng == null) return null;
  if (Math.abs(lat) < 0.01 || Math.abs(lng) < 0.01) return null;
  return { lat, lng };
}

export function isValidJobCoords(point?: LatLngPoint | null): boolean {
  return Boolean(point && Math.abs(point.lat) > 0.01 && Math.abs(point.lng) > 0.01);
}

export function extractPickupCoords(data: Record<string, unknown>): LatLngPoint | null {
  return (
    extractLatLng(data.pickup) ||
    extractLatLng(data.pickupCoords) ||
    extractLatLng(data.pickupLocation) ||
    extractLatLng(data.stationLocation) ||
    extractLatLng(data.vendor) ||
    null
  );
}

export function extractDropoffCoords(data: Record<string, unknown>): LatLngPoint | null {
  return (
    extractLatLng(data.dropoff) ||
    extractLatLng(data.dropoffCoords) ||
    extractLatLng(data.deliveryLocation) ||
    extractLatLng(data.delivery) ||
    null
  );
}

export function buildGoogleMapsDirectionsUrl(
  destinationAddress: string,
  originAddress?: string
): string {
  const dest = encodeURIComponent(destinationAddress);
  if (originAddress) {
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(originAddress)}&destination=${dest}&travelmode=driving`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}

/** Prefer lat/lng so navigation opens the exact pin (not a fuzzy address search). */
export function buildGoogleMapsDirectionsUrlToPoint(
  destination: LatLngPoint,
  origin?: LatLngPoint | null
): string {
  const dest = `${destination.lat},${destination.lng}`;
  if (origin && isValidJobCoords(origin)) {
    return `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${dest}&travelmode=driving`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}&travelmode=driving`;
}
